import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, X, SlidersHorizontal, Grid2X2, Grid3X3, Square, LayoutGrid, Hash } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import ListingCard from '../components/ListingCard';
import TradieCarousel from '../components/TradieCarousel';

import { API_URL } from '../utils/api';

const BrowsePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState({ states: [], major_cities: [] });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [gridLayout, setGridLayout] = useState(() => {
    // Get saved preference or default to 2 columns on mobile
    return localStorage.getItem('browseGridLayout') || '2';
  });

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    condition: searchParams.get('condition') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    location: searchParams.get('location') || '',
    isRestoration: searchParams.get('isRestoration') === 'true',
    listingType: searchParams.get('listing_type') || ''
  });

  // Save layout preference
  const handleLayoutChange = (layout) => {
    setGridLayout(layout);
    localStorage.setItem('browseGridLayout', layout);
  };

  useEffect(() => {
    fetchCategories();
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchListings();
  }, [searchParams]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/locations`);
      setLocations(res.data);
    } catch (err) {
      console.error('Failed to fetch locations', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`);
      setCategories(res.data.categories);
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  };

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      searchParams.forEach((value, key) => {
        if (value) params.append(key, value);
      });
      
      const res = await axios.get(`${API_URL}/api/listings?${params.toString()}`);
      setListings(res.data);
    } catch (err) {
      console.error('Failed to fetch listings', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.category) params.set('category', filters.category);
    if (filters.condition) params.set('condition', filters.condition);
    if (filters.minPrice) params.set('min_price', filters.minPrice);
    if (filters.maxPrice) params.set('max_price', filters.maxPrice);
    if (filters.location) params.set('location', filters.location);
    if (filters.isRestoration) params.set('is_restoration', 'true');
    if (filters.listingType) params.set('listing_type', filters.listingType);
    setSearchParams(params);
    setShowFilters(false);
  };

  // Quick chip toggle: items / workshops / all
  const setListingTypeFilter = (type) => {
    const newFilters = { ...filters, listingType: type };
    setFilters(newFilters);
    const params = new URLSearchParams(searchParams);
    if (type) params.set('listing_type', type);
    else params.delete('listing_type');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      condition: '',
      minPrice: '',
      maxPrice: '',
      location: '',
      isRestoration: false,
      listingType: ''
    });
    setSearchParams({});
  };

  const conditions = [
    { value: 'excellent', label: 'Excellent' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'needs-work', label: 'Needs Work' }
  ];

  const activeFilterCount = [
    filters.category,
    filters.condition,
    filters.minPrice,
    filters.maxPrice,
    filters.location,
    filters.isRestoration
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Browse Items
          </h1>
          <p className="text-stone-500 mt-2">
            Discover unique handcrafted and refurbished pieces from artisans across Australia
          </p>
        </div>

        {/* Type Filter Chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="listing-type-chips">
          {[
            { value: '', label: 'All' },
            { value: 'item', label: 'Items' },
            { value: 'workshop', label: 'Workshops & Classes' },
            { value: 'hands_on_workshop', label: 'Hands-on' },
            { value: 'look_and_learn', label: 'Look & Learn' }
          ].map(opt => {
            const active = (filters.listingType || '') === opt.value;
            return (
              <button
                key={opt.value || 'all'}
                onClick={() => setListingTypeFilter(opt.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${active ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-stone-700 border-stone-200 hover:border-teal-300'}`}
                data-testid={`filter-type-${opt.value || 'all'}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              type="text"
              placeholder="Search items..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              className="pl-10 bg-white"
              data-testid="search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="relative"
            data-testid="filter-toggle-btn"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 bg-gradient-to-r from-sky-500 to-teal-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
          <Button onClick={applyFilters} className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white" data-testid="search-btn">
            Search
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white border border-stone-200 rounded-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-stone-900">Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                data-testid="clear-filters-btn"
              >
                Clear all
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Category</label>
                <Select
                  value={filters.category || "all"}
                  onValueChange={(value) => setFilters({ ...filters, category: value === "all" ? "" : value })}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Condition</label>
                <Select
                  value={filters.condition || "all"}
                  onValueChange={(value) => setFilters({ ...filters, condition: value === "all" ? "" : value })}
                >
                  <SelectTrigger data-testid="condition-select">
                    <SelectValue placeholder="Any condition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any condition</SelectItem>
                    {conditions.map((cond) => (
                      <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Price Range</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filters.minPrice}
                    onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                    className="w-1/2"
                    data-testid="min-price-input"
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                    className="w-1/2"
                    data-testid="max-price-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Location</label>
                <Select
                  value={filters.location || "all"}
                  onValueChange={(value) => setFilters({ ...filters, location: value === "all" ? "" : value })}
                >
                  <SelectTrigger data-testid="location-select">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.states.map((state) => (
                      <SelectItem key={state.id} value={state.abbr}>{state.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Checkbox
                id="restoration"
                checked={filters.isRestoration}
                onCheckedChange={(checked) => setFilters({ ...filters, isRestoration: checked })}
                data-testid="restoration-checkbox"
              />
              <label htmlFor="restoration" className="text-sm text-stone-700 cursor-pointer">
                Show only restoration projects (Before & After)
              </label>
            </div>
          </div>
        )}

        {/* Active Filters Tags */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {filters.category && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-600 rounded-full text-sm">
                {categories.find(c => c.id === filters.category)?.name}
                <button onClick={() => setFilters({ ...filters, category: '' })}><X className="h-3 w-3" /></button>
              </span>
            )}
            {filters.condition && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-600 rounded-full text-sm">
                {conditions.find(c => c.value === filters.condition)?.label}
                <button onClick={() => setFilters({ ...filters, condition: '' })}><X className="h-3 w-3" /></button>
              </span>
            )}
            {(filters.minPrice || filters.maxPrice) && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-600 rounded-full text-sm">
                ${filters.minPrice || '0'} - ${filters.maxPrice || '∞'}
                <button onClick={() => setFilters({ ...filters, minPrice: '', maxPrice: '' })}><X className="h-3 w-3" /></button>
              </span>
            )}
            {filters.location && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-600 rounded-full text-sm">
                {filters.location}
                <button onClick={() => setFilters({ ...filters, location: '' })}><X className="h-3 w-3" /></button>
              </span>
            )}
            {filters.isRestoration && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-600 rounded-full text-sm">
                Restorations only
                <button onClick={() => setFilters({ ...filters, isRestoration: false })}><X className="h-3 w-3" /></button>
              </span>
            )}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className={`grid ${gridLayout === '16' ? 'gap-1' : 'gap-4'} ${
            gridLayout === '1' ? 'grid-cols-1' :
            gridLayout === '2' ? 'grid-cols-2' :
            gridLayout === '4' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4' :
            gridLayout === '16' ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8' :
            'grid-cols-2 sm:grid-cols-4'
          }`}>
            {[...Array(gridLayout === '16' ? 16 : 8)].map((_, i) => (
              <div key={i} className={`bg-stone-100 animate-pulse ${gridLayout === '16' ? 'aspect-square' : 'aspect-[4/5]'} rounded-sm`} />
            ))}
          </div>
        ) : listings.length > 0 ? (
          <>
            {/* Results Header with Layout Switcher */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-stone-500">{listings.length} items found</p>
              
              {/* Layout Switcher - All screens */}
              <div className="flex items-center gap-1" data-testid="grid-layout-switcher">
                <span className="text-xs text-stone-400 mr-2">View:</span>
                <button
                  onClick={() => handleLayoutChange('1')}
                  className={`p-1.5 rounded ${gridLayout === '1' ? 'bg-teal-100 text-teal-600' : 'text-stone-400 hover:bg-stone-100'}`}
                  title="1 per row"
                  data-testid="grid-layout-1"
                >
                  <Square className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleLayoutChange('2')}
                  className={`p-1.5 rounded ${gridLayout === '2' ? 'bg-teal-100 text-teal-600' : 'text-stone-400 hover:bg-stone-100'}`}
                  title="2 per row"
                  data-testid="grid-layout-2"
                >
                  <Grid2X2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleLayoutChange('4')}
                  className={`p-1.5 rounded ${gridLayout === '4' ? 'bg-teal-100 text-teal-600' : 'text-stone-400 hover:bg-stone-100'}`}
                  title="4 per row"
                  data-testid="grid-layout-4"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleLayoutChange('16')}
                  className={`p-1.5 rounded flex items-center gap-0.5 ${gridLayout === '16' ? 'bg-teal-100 text-teal-600' : 'text-stone-400 hover:bg-stone-100'}`}
                  title="Dense view (more items)"
                  data-testid="grid-layout-16"
                >
                  <Hash className="h-4 w-4" />
                  <span className="text-[10px] font-semibold">16</span>
                </button>
              </div>
            </div>
            
            <div className={`grid ${gridLayout === '16' ? 'gap-1' : 'gap-4'} ${
              gridLayout === '1' ? 'grid-cols-1' :
              gridLayout === '2' ? 'grid-cols-2' :
              gridLayout === '4' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4' :
              gridLayout === '16' ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8' :
              'grid-cols-2 sm:grid-cols-4'
            }`}>
              {listings.map((listing, idx) => (
                <ListingCard key={listing.id} listing={listing} index={idx} compact={gridLayout === '16'} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16 bg-white border border-stone-200 rounded-sm">
            <Search className="h-12 w-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-stone-900 mb-2">No items found</h3>
            <p className="text-stone-500 mb-4">Try adjusting your filters or search terms</p>
            <Button onClick={clearFilters} variant="outline">
              Clear filters
            </Button>
          </div>
        )}
      </div>

      {/* Tradie Carousel */}
      <TradieCarousel />
    </div>
  );
};

export default BrowsePage;
